import React, { useMemo, useState } from 'react';
import { X, ChevronDown, Plus, ChevronRight, ArrowLeft, Mail, Calendar, CreditCard, Ticket, FileText, MessageSquare, DollarSign, Users, Settings, Code, BarChart3, LineChart, PieChart, LayoutGrid, List, Lock, Globe, Copy } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { CoreListsTab, CoreListDetail } from './CoreLists';
type Source = 'Email' | 'Jira' | 'Slack' | 'Calendar' | 'Transactions' | 'Confluence';
type SortOption = 'Newest first' | 'Oldest first' | 'Priority';
type GroupOption = 'None' | 'Sender' | 'Category' | 'Date';
type ListType = 'core' | 'standard' | 'computed' | 'embeds';
type ComputedFunction = 'count' | 'sum' | 'avg' | 'mean';
type ViewStyle = 'list' | 'grid' | 'bar' | 'line' | 'pie';
const SOURCE_ICONS: Record<string, React.FC<{
  size?: number;
  className?: string;
}>> = {
  Email: Mail,
  Jira: Ticket,
  Slack: MessageSquare,
  Calendar: Calendar,
  Transactions: DollarSign,
  Confluence: FileText,
  Contacts: Users,
  Finance: CreditCard
};
const SOURCE_FILTERS: Record<Source, string[]> = {
  Email: ['Unread', 'Has attachment', 'Starred', 'High priority', 'Last 7 days', 'Last 30 days', 'From me', 'To me'],
  Jira: ['Assigned to me', 'Unresolved', 'High priority', 'Bug', 'Story', 'Updated recently', 'Created this week', 'In sprint'],
  Slack: ['Mentions me', 'Has reactions', 'Has files', 'In thread', 'Last 7 days', 'Last 30 days'],
  Calendar: ['Today', 'This week', 'Next 7 days', 'Recurring', 'All-day events', 'With attendees'],
  Transactions: ['Last 7 days', 'Last 30 days', 'Over \u00a3100', 'Over \u00a3500', 'Income', 'Expenses', 'Pending'],
  Confluence: ['Updated this week', 'Updated this month', 'Created by me', 'In my spaces', 'Blog posts', 'Pages only']
};
const SOURCE_NUMERIC_FIELDS: Record<Source, string[]> = {
  Email: ['Thread length', 'Attachment count', 'Reply count'],
  Jira: ['Story points', 'Time spent', 'Comment count', 'Subtask count'],
  Slack: ['Reaction count', 'Reply count', 'Thread length'],
  Calendar: ['Duration', 'Attendee count'],
  Transactions: ['Amount', 'Fee', 'Tax'],
  Confluence: ['View count', 'Comment count', 'Version count']
};
const SOURCE_SORT: Record<Source, SortOption[]> = {
  Email: ['Newest first', 'Oldest first', 'Priority'],
  Jira: ['Newest first', 'Oldest first', 'Priority'],
  Slack: ['Newest first', 'Oldest first'],
  Calendar: ['Newest first', 'Oldest first'],
  Transactions: ['Newest first', 'Oldest first'],
  Confluence: ['Newest first', 'Oldest first']
};
const SOURCE_GROUP: Record<Source, GroupOption[]> = {
  Email: ['None', 'Sender', 'Date'],
  Jira: ['None', 'Category', 'Date'],
  Slack: ['None', 'Date'],
  Calendar: ['None', 'Date'],
  Transactions: ['None', 'Category', 'Date'],
  Confluence: ['None', 'Date']
};
interface Filter {
  id: string;
  label: string;
}
interface ResultItem {
  person: string;
  lines: string[];
  time: string;
  meta?: string;
}
interface ListDef {
  id: string;
  title: string;
  description: string;
  source: Source;
  icon: string;
  type: 'standard' | 'computed';
  results: ResultItem[];
}
interface EmbedDef {
  id: string;
  title: string;
  sourceList: string;
  sourceListType: 'standard' | 'computed';
  viewStyle: ViewStyle;
  permissions: string[];
  isPublic: boolean;
  created: string;
}
const LISTS: ListDef[] = [{
  id: '1',
  title: 'Priority emails this week',
  description: 'High priority emails from the last 7 days',
  source: 'Email',
  icon: 'Email',
  type: 'standard',
  results: [{
    person: 'Sarah',
    lines: ['needs Q2 budget approval (Friday)'],
    time: 'this morning'
  }, {
    person: 'James, Acme Legal',
    lines: ['wants section 4.2 liability cap adjusted', 'sent DPA review comments', 'wants a 15-min call (Friday)'],
    time: 'this morning'
  }, {
    person: 'Paul, Partnerships',
    lines: ['needs privacy policy URL (for tomorrow)'],
    time: 'this morning'
  }, {
    person: 'Tom',
    lines: ['flagged 429s on /sync at peak', 'proposing token-bucket rate limiting', 'wants sign-off (before standup tomorrow)'],
    time: 'yesterday'
  }, {
    person: 'Lisa, DevOps',
    lines: ['needs SSL renewal PR approved (Friday)'],
    time: 'yesterday'
  }]
}, {
  id: '2',
  title: "Today's meetings",
  description: 'Calendar events for today',
  source: 'Calendar',
  icon: 'Calendar',
  type: 'standard',
  results: [{
    person: 'Team standup',
    lines: ['daily engineering sync'],
    time: '9:00 AM',
    meta: '15 min'
  }, {
    person: 'Sarah + James',
    lines: ['Q2 budget review'],
    time: '11:00 AM',
    meta: '45 min'
  }, {
    person: 'Acme Corp',
    lines: ['partnership call (review section 4.2)'],
    time: '2:00 PM',
    meta: '30 min'
  }]
}, {
  id: '3',
  title: 'Recent Jira updates',
  description: 'Issues updated in the last 48 hours',
  source: 'Jira',
  icon: 'Jira',
  type: 'standard',
  results: [{
    person: 'Bug',
    lines: ['rate limiting for /sync'],
    time: 'Zen Bot'
  }, {
    person: 'Task',
    lines: ['SSL cert renewal automation'],
    time: 'Zen Bot'
  }, {
    person: 'Story',
    lines: ['Nango webhook pipeline'],
    time: 'Zen Bot'
  }, {
    person: 'Story',
    lines: ['patient onboarding redesign'],
    time: 'TMA'
  }, {
    person: 'Bug',
    lines: ['fix 429 retry logic'],
    time: 'Zen Bot'
  }]
}, {
  id: '4',
  title: 'Transactions this week',
  description: 'All transactions from the last 7 days',
  source: 'Transactions',
  icon: 'Transactions',
  type: 'standard',
  results: [{
    person: 'Hetzner',
    lines: ['server hosting'],
    time: 'today',
    meta: '\u00a347.20'
  }, {
    person: 'Vercel',
    lines: ['Pro plan'],
    time: 'yesterday',
    meta: '\u00a320.00'
  }, {
    person: 'OpenAI',
    lines: ['API usage'],
    time: 'Monday',
    meta: '\u00a3156.80'
  }]
}, {
  id: '5',
  title: 'Monthly spend by category',
  description: 'Aggregated spend grouped by category',
  source: 'Transactions',
  icon: 'Finance',
  type: 'computed',
  results: [{
    person: 'Infrastructure',
    lines: ['Hetzner, Vercel, Cloudflare'],
    time: 'this month',
    meta: '\u00a3312.40'
  }, {
    person: 'AI / APIs',
    lines: ['OpenAI, Anthropic, LiveKit'],
    time: 'this month',
    meta: '\u00a3489.60'
  }, {
    person: 'SaaS',
    lines: ['Linear, Figma, Notion'],
    time: 'this month',
    meta: '\u00a3145.00'
  }]
}, {
  id: '6',
  title: 'Unresolved email threads',
  description: 'Email threads awaiting your reply',
  source: 'Email',
  icon: 'Email',
  type: 'computed',
  results: [{
    person: 'Paul, Partnerships',
    lines: ['needs privacy policy URL'],
    time: 'this morning'
  }]
}, {
  id: '7',
  title: 'Top contacts by interaction',
  description: 'Most frequently contacted this month',
  source: 'Email',
  icon: 'Contacts',
  type: 'computed',
  results: [{
    person: 'Sarah',
    lines: ['24 interactions'],
    time: 'this month'
  }, {
    person: 'Tom',
    lines: ['18 interactions'],
    time: 'this month'
  }]
}];
const EMBEDS: EmbedDef[] = [{
  id: 'e1',
  title: 'Monthly Spend Chart',
  sourceList: 'Monthly spend by category',
  sourceListType: 'computed',
  viewStyle: 'bar',
  permissions: ['Finance Group'],
  isPublic: false,
  created: '2 days ago'
}, {
  id: 'e2',
  title: 'Team Jira Board',
  sourceList: 'Recent Jira updates',
  sourceListType: 'standard',
  viewStyle: 'grid',
  permissions: ['TMA Wide'],
  isPublic: false,
  created: '1 week ago'
}, {
  id: 'e3',
  title: 'Contact Interactions',
  sourceList: 'Top contacts by interaction',
  sourceListType: 'computed',
  viewStyle: 'pie',
  permissions: [],
  isPublic: true,
  created: '5 days ago'
}];
const PERMISSION_GROUPS = ['TMA Wide', 'Finance Group', 'Engineering', 'Leadership', 'External Partners'];
const VIEW_ICONS: Record<ViewStyle, React.FC<{
  size?: number;
  className?: string;
}>> = {
  list: List,
  grid: LayoutGrid,
  bar: BarChart3,
  line: LineChart,
  pie: PieChart
};
const NEW_SOURCES: {
  source: Source;
  icon: string;
  label: string;
}[] = [{
  source: 'Email',
  icon: 'Email',
  label: 'Email list'
}, {
  source: 'Jira',
  icon: 'Jira',
  label: 'Jira list'
}, {
  source: 'Slack',
  icon: 'Slack',
  label: 'Slack list'
}, {
  source: 'Calendar',
  icon: 'Calendar',
  label: 'Calendar list'
}, {
  source: 'Transactions',
  icon: 'Transactions',
  label: 'Transactions list'
}, {
  source: 'Confluence',
  icon: 'Confluence',
  label: 'Confluence list'
}];
export function ListCreator() {
  const [editingId, setEditingId] = useState<string | null>(null);
  const [newSource, setNewSource] = useState<Source | null>(null);
  const [newType, setNewType] = useState<'standard' | 'computed'>('standard');
  const [activeTab, setActiveTab] = useState<ListType>('core');
  const [editingEmbed, setEditingEmbed] = useState<string | null>(null);
  const [creatingEmbed, setCreatingEmbed] = useState(false);
  const [viewingCoreList, setViewingCoreList] = useState<string | null>(null);
  if (viewingCoreList) return <CoreListDetail listId={viewingCoreList} onBack={() => setViewingCoreList(null)} data-id="element-1296" />;
  if (newSource) return <ListEditor list={null} source={newSource} listType={newType} onBack={() => setNewSource(null)} data-id="element-1297" />;
  if (editingId) {
    const list = LISTS.find(l => l.id === editingId);
    if (list) return <ListDetail list={list} onBack={() => setEditingId(null)} data-id="element-1298" />;
  }
  if (editingEmbed) {
    const embed = EMBEDS.find(e => e.id === editingEmbed);
    if (embed) return <EmbedPreview embed={embed} onBack={() => setEditingEmbed(null)} data-id="element-1299" />;
  }
  if (creatingEmbed) return <EmbedBuilder onBack={() => setCreatingEmbed(false)} data-id="element-1300" />;
  return <ListIndex activeTab={activeTab} setActiveTab={setActiveTab} onSelect={id => setEditingId(id)} onNew={(s, t) => {
    setNewSource(s);
    setNewType(t);
  }} onSelectEmbed={id => setEditingEmbed(id)} onNewEmbed={() => setCreatingEmbed(true)} onSelectCore={id => setViewingCoreList(id)} data-id="element-1301" />;
}
function ListIndex({
  activeTab,
  setActiveTab,
  onSelect,
  onNew,
  onSelectEmbed,
  onNewEmbed,
  onSelectCore
}: {
  activeTab: ListType;
  setActiveTab: (t: ListType) => void;
  onSelect: (id: string) => void;
  onNew: (s: Source, t: 'standard' | 'computed') => void;
  onSelectEmbed: (id: string) => void;
  onNewEmbed: () => void;
  onSelectCore: (id: string) => void;
}) {
  const [showNewMenu, setShowNewMenu] = useState(false);
  const filteredLists = LISTS.filter(l => l.type === activeTab);
  return <div className="min-h-[calc(100vh-56px)] text-[#e8e4df] pt-12 pb-32 px-6" data-id="element-1302">
      <div className="max-w-[640px] mx-auto w-full flex flex-col" data-id="element-1303">
        <h1 className="text-[20px] font-medium text-[#e8e4df] tracking-tight mb-6" data-id="element-1304">Lists</h1>

        <nav className="flex items-center gap-6 mb-6 border-b border-[#8a8680]/10" data-id="element-1305">
          {(['core', 'standard', 'computed', 'embeds'] as ListType[]).map(tab => {
          const isActive = activeTab === tab;
          return <button key={tab} onClick={() => setActiveTab(tab)} className={`pb-3 text-[14px] transition-colors relative capitalize ${isActive ? 'text-[#e8e4df]' : 'text-[#8a8680] hover:text-[#e8e4df]/80'}`} data-id="element-1306">{tab}{isActive && <motion.div layoutId="listTabIndicator" className="absolute bottom-0 left-0 right-0 h-[2px] bg-[#4a9a9a]" initial={false} transition={{
              type: 'spring',
              stiffness: 500,
              damping: 30
            }} data-id="element-1307" />}</button>;
        })}
        </nav>

        {activeTab === 'core' && <CoreListsTab onSelect={onSelectCore} data-id="element-1308" />}

        {(activeTab === 'standard' || activeTab === 'computed') && <>
            <div className="flex flex-col gap-3" data-id="element-1309">
              {filteredLists.map((list, i) => {
            const Icon = SOURCE_ICONS[list.icon] || FileText;
            return <motion.button key={list.id} initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.3,
              delay: i * 0.04
            }} onClick={() => onSelect(list.id)} className="bg-[#1c1c22] rounded-[16px] p-5 text-left group hover:bg-[#1e1e25] transition-colors w-full" data-id="element-1310"><div className="flex items-center gap-4" data-id="element-1311"><div className="w-10 h-10 rounded-[12px] bg-[#252530] flex items-center justify-center shrink-0" data-id="element-1312"><Icon size={18} className="text-[#8a8680]" strokeWidth={1.5} data-id="element-1313" /></div><h2 className="flex-1 text-[#e8e4df] text-[15px] truncate" data-id="element-1314">{list.title}</h2><ChevronRight size={16} className="text-[#8a8680]/20 group-hover:text-[#8a8680]/60 transition-colors shrink-0" data-id="element-1315" /></div></motion.button>;
          })}
              {filteredLists.length === 0 && <p className="text-[#8a8680]/40 text-[13px] text-center py-8" data-id="element-1316">Empty.</p>}
            </div>
            <div className="relative mt-6" data-id="element-1317">
              <button onClick={() => setShowNewMenu(!showNewMenu)} className="flex items-center gap-2 text-[#8a8680] hover:text-[#e8e4df] transition-colors px-2" data-id="element-1318"><Plus size={14} data-id="element-1319" /><span className="text-[13px]" data-id="element-1320">New {activeTab} list</span></button>
              <AnimatePresence data-id="element-1321">{showNewMenu && <><div className="fixed inset-0 z-10" onClick={() => setShowNewMenu(false)} data-id="element-1322" /><motion.div initial={{
                opacity: 0,
                y: -8
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: -8
              }} className="absolute left-0 bottom-full mb-2 rounded-[12px] overflow-hidden z-20 w-[220px] bg-[#1c1c22]" data-id="element-1323"><div className="py-2" data-id="element-1324">{NEW_SOURCES.map(({
                    source,
                    icon,
                    label
                  }) => {
                    const Icon = SOURCE_ICONS[icon] || FileText;
                    return <button key={source} onClick={() => {
                      onNew(source, activeTab as 'standard' | 'computed');
                      setShowNewMenu(false);
                    }} className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-[#e8e4df] hover:bg-[#252530]/70 transition-colors" data-id="element-1325"><Icon size={16} className="text-[#8a8680]" data-id="element-1326" />{label}</button>;
                  })}</div></motion.div></>}</AnimatePresence>
            </div>
          </>}

        {activeTab === 'embeds' && <>
            <div className="flex flex-col gap-3" data-id="element-1327">
              {EMBEDS.map((embed, i) => {
            const VIcon = VIEW_ICONS[embed.viewStyle] || List;
            return <motion.button key={embed.id} initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.3,
              delay: i * 0.04
            }} onClick={() => onSelectEmbed(embed.id)} className="bg-[#1c1c22] rounded-[16px] p-5 text-left group hover:bg-[#1e1e25] transition-colors w-full" data-id="element-1328"><div className="flex items-center gap-4" data-id="element-1329"><div className="w-10 h-10 rounded-[12px] bg-[#252530] flex items-center justify-center shrink-0" data-id="element-1330"><VIcon size={18} className="text-[#8a8680]" strokeWidth={1.5} data-id="element-1331" /></div><h2 className="flex-1 text-[#e8e4df] text-[15px] truncate" data-id="element-1332">{embed.title}</h2><ChevronRight size={16} className="text-[#8a8680]/20 group-hover:text-[#8a8680]/60 transition-colors shrink-0" data-id="element-1333" /></div></motion.button>;
          })}
            </div>
            <button onClick={onNewEmbed} className="flex items-center gap-2 text-[#8a8680] hover:text-[#e8e4df] transition-colors px-2 mt-6" data-id="element-1334"><Plus size={14} data-id="element-1335" /><span className="text-[13px]" data-id="element-1336">New embed</span></button>
          </>}
      </div>
    </div>;
}
function EmbedPreview({
  embed,
  onBack
}: {
  embed: EmbedDef;
  onBack: () => void;
}) {
  const [viewStyle, setViewStyle] = useState<ViewStyle>(embed.viewStyle);
  const [permissions, setPermissions] = useState<string[]>(embed.permissions);
  const [isPublic, setIsPublic] = useState(embed.isPublic);
  const [showPermMenu, setShowPermMenu] = useState(false);
  const isComputed = embed.sourceListType === 'computed';
  const viewOptions: ViewStyle[] = isComputed ? ['bar', 'line', 'pie', 'list', 'grid'] : ['list', 'grid'];
  const embedUrl = `https://zenbot.chat/embed/${embed.id}`;
  const togglePerm = (p: string) => setPermissions(prev => prev.includes(p) ? prev.filter(x => x !== p) : [...prev, p]);
  return <motion.div initial={{
    opacity: 0,
    x: 20
  }} animate={{
    opacity: 1,
    x: 0
  }} transition={{
    duration: 0.3
  }} className="min-h-[calc(100vh-56px)] text-[#e8e4df] pt-12 pb-32 px-6" data-id="element-1337">
      <div className="max-w-[640px] mx-auto w-full flex flex-col" data-id="element-1338">
        <button onClick={onBack} className="flex items-center gap-2 text-[#8a8680]/60 hover:text-[#e8e4df] transition-colors -ml-1 mb-6" data-id="element-1339"><ArrowLeft size={14} data-id="element-1340" /><span className="text-[13px]" data-id="element-1341">Embeds</span></button>
        <h1 className="text-[20px] font-medium text-[#e8e4df] mb-8" data-id="element-1342">{embed.title}</h1>
        <div className="bg-[#1c1c22] rounded-[16px] p-6 mb-6" data-id="element-1343">
          <div className="flex items-center justify-end mb-4" data-id="element-1344"><div className="flex items-center gap-1 bg-[#252530] rounded-lg p-1" data-id="element-1345">{viewOptions.map(v => {
              const VIcon = VIEW_ICONS[v];
              return <button key={v} onClick={() => setViewStyle(v)} className={`p-1.5 rounded-md transition-colors ${viewStyle === v ? 'bg-[#4a9a9a]/20 text-[#4a9a9a]' : 'text-[#8a8680]/50 hover:text-[#8a8680]'}`} data-id="element-1346"><VIcon size={14} data-id="element-1347" /></button>;
            })}</div></div>
          {(viewStyle === 'list' || viewStyle === 'grid') && <div className={viewStyle === 'grid' ? 'grid grid-cols-2 gap-3' : 'flex flex-col gap-2'} data-id="element-1348">{['Infrastructure', 'AI / APIs', 'SaaS'].map((item, i) => <div key={i} className={`bg-[#252530]/50 rounded-[12px] ${viewStyle === 'grid' ? 'p-4' : 'px-4 py-3'}`} data-id="element-1349"><span className="text-[14px] text-[#e8e4df]/80" data-id="element-1350">{item}</span></div>)}</div>}
          {viewStyle === 'bar' && <div className="flex items-end gap-4 h-[140px] px-4 pt-4" data-id="element-1351">{[{
            h: 65,
            l: 'Infra'
          }, {
            h: 100,
            l: 'AI'
          }, {
            h: 45,
            l: 'SaaS'
          }].map((b, i) => <div key={i} className="flex-1 flex flex-col items-center gap-2" data-id="element-1352"><div className="w-full bg-[#4a9a9a]/30 rounded-t-md" style={{
              height: `${b.h}%`
            }} data-id="element-1353" /><span className="text-[11px] text-[#8a8680]/50" data-id="element-1354">{b.l}</span></div>)}</div>}
          {viewStyle === 'line' && <div className="h-[140px] flex items-center justify-center" data-id="element-1355"><svg viewBox="0 0 300 100" className="w-full h-full" data-id="element-1356"><polyline points="20,80 100,30 180,50 260,20" fill="none" stroke="#4a9a9a" strokeWidth="2" strokeLinecap="round" data-id="element-1357" />{[[20, 80], [100, 30], [180, 50], [260, 20]].map(([x, y], i) => <circle key={i} cx={x} cy={y} r="4" fill="#4a9a9a" data-id="element-1358" />)}</svg></div>}
          {viewStyle === 'pie' && <div className="h-[140px] flex items-center justify-center" data-id="element-1359"><svg viewBox="0 0 100 100" className="w-[140px] h-[140px]" data-id="element-1360"><circle cx="50" cy="50" r="40" fill="none" stroke="#4a9a9a" strokeWidth="20" strokeDasharray="78 173" data-id="element-1361" /><circle cx="50" cy="50" r="40" fill="none" stroke="#4a9a9a" strokeWidth="20" strokeDasharray="60 191" strokeDashoffset="-78" opacity="0.6" data-id="element-1362" /><circle cx="50" cy="50" r="40" fill="none" stroke="#4a9a9a" strokeWidth="20" strokeDasharray="35 216" strokeDashoffset="-138" opacity="0.3" data-id="element-1363" /></svg></div>}
        </div>
        <div className="bg-[#1c1c22] rounded-[16px] p-6 mb-6" data-id="element-1364">
          <div className="flex items-center justify-between py-3" data-id="element-1365"><span className="text-sm text-[#8a8680]" data-id="element-1366">Access</span><div className="flex items-center gap-2" data-id="element-1367"><button onClick={() => setIsPublic(false)} className={`text-[13px] px-3 py-1 rounded-full transition-colors ${!isPublic ? 'bg-[#4a9a9a]/20 text-[#4a9a9a]' : 'bg-[#252530] text-[#8a8680]'}`} data-id="element-1368"><Lock size={10} className="inline mr-1 -mt-0.5" data-id="element-1369" />Private</button><button onClick={() => setIsPublic(true)} className={`text-[13px] px-3 py-1 rounded-full transition-colors ${isPublic ? 'bg-[#4a9a9a]/20 text-[#4a9a9a]' : 'bg-[#252530] text-[#8a8680]'}`} data-id="element-1370"><Globe size={10} className="inline mr-1 -mt-0.5" data-id="element-1371" />Public</button></div></div>
          <div className="h-px bg-[#8a8680]/10" data-id="element-1372" />
          {!isPublic && <div className="py-3" data-id="element-1373"><div className="flex items-center justify-between mb-3" data-id="element-1374"><span className="text-sm text-[#8a8680]" data-id="element-1375">Permission groups</span><div className="relative" data-id="element-1376"><button onClick={() => setShowPermMenu(!showPermMenu)} className="text-[#8a8680] hover:text-[#e8e4df]" data-id="element-1377"><Plus size={14} data-id="element-1378" /></button>{showPermMenu && <><div className="fixed inset-0 z-10" onClick={() => setShowPermMenu(false)} data-id="element-1379" /><div className="absolute right-0 mt-2 rounded-lg z-20 w-[180px] bg-[#252530]" data-id="element-1380">{PERMISSION_GROUPS.map(p => <button key={p} onClick={() => {
                    togglePerm(p);
                    setShowPermMenu(false);
                  }} className="w-full text-left px-3 py-2 text-sm hover:bg-[#4a9a9a]/10" style={{
                    color: permissions.includes(p) ? '#4a9a9a' : '#e8e4df'
                  }} data-id="element-1381">{p}</button>)}</div></>}</div></div><div className="flex flex-wrap gap-2" data-id="element-1382">{permissions.map(p => <span key={p} className="inline-flex items-center gap-1.5 text-[13px] bg-[#252530] text-[#8a8680] px-3 py-1 rounded-full" data-id="element-1383">{p}<button onClick={() => togglePerm(p)} className="hover:text-[#e8e4df]" data-id="element-1384"><X size={10} data-id="element-1385" /></button></span>)}{permissions.length === 0 && <span className="text-[13px] text-[#8a8680]/40" data-id="element-1386">Email OTP required</span>}</div></div>}
          <div className="h-px bg-[#8a8680]/10" data-id="element-1387" />
          <div className="flex items-center justify-between py-3" data-id="element-1388"><span className="text-sm text-[#8a8680]" data-id="element-1389">Embed URL</span><button className="flex items-center gap-1.5 text-[13px] text-[#4a9a9a] hover:text-[#5bb5b5]" data-id="element-1390"><Copy size={12} data-id="element-1391" />Copy</button></div>
          <div className="bg-[#252530] rounded-lg px-3 py-2 text-[13px] text-[#8a8680]/60 font-mono break-all" data-id="element-1392">{embedUrl}</div>
        </div>
        <div className="flex items-center justify-center gap-6" data-id="element-1393"><button className="text-[14px] text-[#4a9a9a]/80 hover:text-[#4a9a9a]" data-id="element-1394">Save</button><button onClick={onBack} className="text-[14px] text-[#8a8680]/50 hover:text-[#e8e4df]/80" data-id="element-1395">Cancel</button></div>
      </div>
    </motion.div>;
}
function EmbedBuilder({
  onBack
}: {
  onBack: () => void;
}) {
  const [title, setTitle] = useState('');
  const [selectedList, setSelectedList] = useState('');
  const [viewStyle, setViewStyle] = useState<ViewStyle>('list');
  const [isPublic, setIsPublic] = useState(false);
  const [permissions, setPermissions] = useState<string[]>([]);
  const [showListMenu, setShowListMenu] = useState(false);
  const [showPermMenu, setShowPermMenu] = useState(false);
  const selected = LISTS.find(l => l.title === selectedList);
  const isComputed = selected?.type === 'computed';
  const viewOptions: ViewStyle[] = isComputed ? ['bar', 'line', 'pie', 'list', 'grid'] : ['list', 'grid'];
  const togglePerm = (p: string) => setPermissions(prev => prev.includes(p) ? prev.filter(x => x !== p) : [...prev, p]);
  return <motion.div initial={{
    opacity: 0,
    x: 20
  }} animate={{
    opacity: 1,
    x: 0
  }} className="min-h-[calc(100vh-56px)] w-full flex items-center justify-center p-6" data-id="element-1396">
      <div className="w-full max-w-[640px] space-y-8" data-id="element-1397">
        <button onClick={onBack} className="flex items-center gap-2 text-[#8a8680]/60 hover:text-[#e8e4df] transition-colors -ml-1" data-id="element-1398"><ArrowLeft size={14} data-id="element-1399" /><span className="text-[13px]" data-id="element-1400">Embeds</span></button>
        <input type="text" value={title} onChange={e => setTitle(e.target.value)} placeholder="Name this embed" className="w-full bg-transparent text-[20px] font-medium outline-none text-[#e8e4df] placeholder:text-[#8a8680]/30" data-id="element-1401" />
        <div className="rounded-2xl p-8 bg-[#1c1c22]" data-id="element-1402">
          <div className="flex items-center justify-between py-4" data-id="element-1403"><span className="text-sm text-[#8a8680]" data-id="element-1404">Source list</span><div className="relative" data-id="element-1405"><button onClick={() => setShowListMenu(!showListMenu)} className="flex items-center gap-2 px-3 py-1.5 rounded bg-[#252530] text-[#e8e4df] text-sm min-w-[200px] justify-between" data-id="element-1406"><span className="truncate" data-id="element-1407">{selectedList || 'Select...'}</span><ChevronDown size={14} className="text-[#8a8680]" data-id="element-1408" /></button>{showListMenu && <><div className="fixed inset-0 z-10" onClick={() => setShowListMenu(false)} data-id="element-1409" /><div className="absolute right-0 mt-2 rounded-lg z-20 w-[260px] bg-[#252530] max-h-[300px] overflow-y-auto" data-id="element-1410">{LISTS.map(l => <button key={l.id} onClick={() => {
                  setSelectedList(l.title);
                  setShowListMenu(false);
                  setViewStyle(l.type === 'computed' ? 'bar' : 'list');
                }} className="w-full text-left px-3 py-2.5 text-sm hover:bg-[#4a9a9a]/10 flex justify-between" style={{
                  color: selectedList === l.title ? '#4a9a9a' : '#e8e4df'
                }} data-id="element-1411"><span className="truncate" data-id="element-1412">{l.title}</span><span className="text-[11px] text-[#8a8680]/40 ml-2" data-id="element-1413">{l.type}</span></button>)}</div></>}</div></div>
          {selectedList && <><div className="h-px bg-[#8a8680]/10" data-id="element-1414" /><div className="flex items-center justify-between py-4" data-id="element-1415"><span className="text-sm text-[#8a8680]" data-id="element-1416">View style</span><div className="flex items-center gap-1 bg-[#252530] rounded-lg p-1" data-id="element-1417">{viewOptions.map(v => {
                const VIcon = VIEW_ICONS[v];
                return <button key={v} onClick={() => setViewStyle(v)} className={`p-1.5 rounded-md ${viewStyle === v ? 'bg-[#4a9a9a]/20 text-[#4a9a9a]' : 'text-[#8a8680]/50 hover:text-[#8a8680]'}`} data-id="element-1418"><VIcon size={14} data-id="element-1419" /></button>;
              })}</div></div></>}
          <div className="h-px bg-[#8a8680]/10" data-id="element-1420" />
          <div className="flex items-center justify-between py-4" data-id="element-1421"><span className="text-sm text-[#8a8680]" data-id="element-1422">Access</span><div className="flex gap-2" data-id="element-1423"><button onClick={() => setIsPublic(false)} className={`text-[13px] px-3 py-1 rounded-full ${!isPublic ? 'bg-[#4a9a9a]/20 text-[#4a9a9a]' : 'bg-[#252530] text-[#8a8680]'}`} data-id="element-1424"><Lock size={10} className="inline mr-1 -mt-0.5" data-id="element-1425" />Private</button><button onClick={() => setIsPublic(true)} className={`text-[13px] px-3 py-1 rounded-full ${isPublic ? 'bg-[#4a9a9a]/20 text-[#4a9a9a]' : 'bg-[#252530] text-[#8a8680]'}`} data-id="element-1426"><Globe size={10} className="inline mr-1 -mt-0.5" data-id="element-1427" />Public</button></div></div>
          {!isPublic && <><div className="h-px bg-[#8a8680]/10" data-id="element-1428" /><div className="py-4" data-id="element-1429"><div className="flex items-center justify-between mb-3" data-id="element-1430"><span className="text-sm text-[#8a8680]" data-id="element-1431">Permission groups</span><div className="relative" data-id="element-1432"><button onClick={() => setShowPermMenu(!showPermMenu)} className="text-[#8a8680] hover:text-[#e8e4df]" data-id="element-1433"><Plus size={14} data-id="element-1434" /></button>{showPermMenu && <><div className="fixed inset-0 z-10" onClick={() => setShowPermMenu(false)} data-id="element-1435" /><div className="absolute right-0 mt-2 rounded-lg z-20 w-[180px] bg-[#252530]" data-id="element-1436">{PERMISSION_GROUPS.map(p => <button key={p} onClick={() => {
                      togglePerm(p);
                      setShowPermMenu(false);
                    }} className="w-full text-left px-3 py-2 text-sm hover:bg-[#4a9a9a]/10" style={{
                      color: permissions.includes(p) ? '#4a9a9a' : '#e8e4df'
                    }} data-id="element-1437">{p}</button>)}</div></>}</div></div><div className="flex flex-wrap gap-2" data-id="element-1438">{permissions.map(p => <span key={p} className="inline-flex items-center gap-1.5 text-[13px] bg-[#252530] text-[#8a8680] px-3 py-1 rounded-full" data-id="element-1439">{p}<button onClick={() => togglePerm(p)} className="hover:text-[#e8e4df]" data-id="element-1440"><X size={10} data-id="element-1441" /></button></span>)}{permissions.length === 0 && <span className="text-[13px] text-[#8a8680]/40" data-id="element-1442">Email OTP required</span>}</div></div></>}
        </div>
        <div className="flex justify-center gap-6" data-id="element-1443"><button className="text-[14px] text-[#4a9a9a]/80 hover:text-[#4a9a9a]" data-id="element-1444">Create</button><button onClick={onBack} className="text-[14px] text-[#8a8680]/50 hover:text-[#e8e4df]/80" data-id="element-1445">Cancel</button></div>
      </div>
    </motion.div>;
}
function ListDetail({
  list,
  onBack
}: {
  list: ListDef;
  onBack: () => void;
}) {
  const [showConfig, setShowConfig] = useState(false);
  if (showConfig) return <ListEditor list={list} source={list.source} listType={list.type} onBack={() => setShowConfig(false)} backLabel="Results" data-id="element-1446" />;
  return <motion.div initial={{
    opacity: 0,
    x: 20
  }} animate={{
    opacity: 1,
    x: 0
  }} className="min-h-[calc(100vh-56px)] text-[#e8e4df] pt-12 pb-32 px-6" data-id="element-1447">
      <div className="max-w-[640px] mx-auto w-full flex flex-col" data-id="element-1448">
        <button onClick={onBack} className="flex items-center gap-2 text-[#8a8680]/60 hover:text-[#e8e4df] transition-colors -ml-1 mb-8" data-id="element-1449"><ArrowLeft size={14} data-id="element-1450" /><span className="text-[13px]" data-id="element-1451">Lists</span></button>
        <header className="mb-10 flex items-start justify-between" data-id="element-1452">
          <h1 className="text-[20px] font-medium text-[#e8e4df] tracking-tight" data-id="element-1453">{list.title}</h1>
          <button onClick={() => setShowConfig(true)} className="text-[#8a8680]/30 hover:text-[#8a8680] transition-colors" title="Configure" data-id="element-1454"><Settings size={15} data-id="element-1455" /></button>
        </header>
        <div className="flex flex-col gap-3" data-id="element-1456">
          {list.results.map((item, i) => <motion.div key={i} initial={{
          opacity: 0,
          y: 8
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.4,
          delay: i * 0.04
        }} className="bg-[#1c1c22] rounded-[16px] p-5" data-id="element-1457">
              <p className="font-semibold text-[#e8e4df] text-[15px] leading-[1.5]" data-id="element-1458">{item.person}</p>
              <div className="mt-2 space-y-1" data-id="element-1459">
                {item.lines.map((line, j) => <p key={j} className="text-[#e8e4df]/75 text-[14px] leading-[1.55]" data-id="element-1460">{line}</p>)}
              </div>
              <p className="text-[12px] text-[#8a8680]/50 mt-3" data-id="element-1461">
                {item.time}{item.meta && <span data-id="element-1462"> \u00b7 {item.meta}</span>}
              </p>
            </motion.div>)}
        </div>
      </div>
    </motion.div>;
}
function ListEditor({
  list,
  source,
  listType,
  onBack,
  backLabel
}: {
  list: ListDef | null;
  source: Source;
  listType: 'standard' | 'computed';
  onBack: () => void;
  backLabel?: string;
}) {
  const [title, setTitle] = useState(list?.title || '');
  const availableFilters = SOURCE_FILTERS[source] || [];
  const availableSorts = SOURCE_SORT[source] || ['Newest first', 'Oldest first'];
  const availableGroups = SOURCE_GROUP[source] || ['None'];
  const numericFields = SOURCE_NUMERIC_FIELDS[source] || [];
  const [filters, setFilters] = useState<Filter[]>(list ? [{
    id: '1',
    label: availableFilters[0] || 'Last 7 days'
  }] : []);
  const [sort, setSort] = useState<SortOption>(availableSorts[0]);
  const [groupBy, setGroupBy] = useState<GroupOption>(availableGroups[0]);
  const [limit, setLimit] = useState(25);
  const [showFilterMenu, setShowFilterMenu] = useState(false);
  const [computedFn, setComputedFn] = useState<ComputedFunction>('count');
  const [computedField, setComputedField] = useState(numericFields[0] || '');
  const SourceIcon = SOURCE_ICONS[source] || FileText;
  const needsField = computedFn === 'sum' || computedFn === 'avg' || computedFn === 'mean';
  const autoDescription = useMemo(() => {
    const sn = source === 'Email' ? 'Emails' : source;
    const ft = filters.length > 0 ? `, filtered by ${filters.map(f => f.label.toLowerCase()).join(', ')}` : '';
    const st = sort === 'Newest first' ? 'sorted newest first' : sort === 'Oldest first' ? 'sorted oldest first' : 'sorted by priority';
    if (listType === 'computed') {
      const fn = needsField ? `${computedFn} of ${computedField.toLowerCase()}` : 'count';
      return `${sn}${ft}, ${fn}, grouped by ${groupBy.toLowerCase()}`;
    }
    ;
    return `${sn}${ft}, ${st}`;
  }, [source, filters, sort, listType, computedFn, computedField, groupBy, needsField]);
  const removeFilter = (id: string) => setFilters(filters.filter(f => f.id !== id));
  const addFilter = (label: string) => {
    if (!filters.find(f => f.label === label)) setFilters([...filters, {
      id: String(Date.now()),
      label
    }]);
    setShowFilterMenu(false);
  };
  return <motion.div initial={{
    opacity: 0,
    x: 20
  }} animate={{
    opacity: 1,
    x: 0
  }} className="min-h-[calc(100vh-56px)] w-full flex items-center justify-center p-6" data-id="element-1463">
      <div className="w-full max-w-[640px] space-y-8" data-id="element-1464">
        <button onClick={onBack} className="flex items-center gap-2 text-[#8a8680]/60 hover:text-[#e8e4df] -ml-1" data-id="element-1465"><ArrowLeft size={14} data-id="element-1466" /><span className="text-[13px]" data-id="element-1467">{backLabel || 'Lists'}</span></button>
        <div className="space-y-3" data-id="element-1468"><div className="flex items-center gap-3" data-id="element-1469"><div className="w-10 h-10 rounded-[12px] bg-[#252530] flex items-center justify-center" data-id="element-1470"><SourceIcon size={18} className="text-[#8a8680]" data-id="element-1471" /></div><span className="text-sm text-[#8a8680]" data-id="element-1472">{source}</span></div><input type="text" value={title} onChange={e => setTitle(e.target.value)} placeholder="Name this list" className="w-full bg-transparent text-[20px] font-medium outline-none text-[#e8e4df] placeholder:text-[#8a8680]/30" data-id="element-1473" /><p className="text-[13px] text-[#8a8680]/60" data-id="element-1474">{autoDescription}</p></div>
        <div className="rounded-2xl p-8 bg-[#1c1c22]" data-id="element-1475">
          {listType === 'computed' && <><div className="flex items-center justify-between py-4" data-id="element-1476"><span className="text-sm text-[#8a8680]" data-id="element-1477">Function</span><Select value={computedFn} onChange={setComputedFn} options={['count', 'sum', 'avg', 'mean']} data-id="element-1478" /></div><div className="h-px bg-[#8a8680]/10" data-id="element-1479" />{needsField && <><div className="flex items-center justify-between py-4" data-id="element-1480"><span className="text-sm text-[#8a8680]" data-id="element-1481">Field</span><Select value={computedField} onChange={setComputedField} options={numericFields} data-id="element-1482" /></div><div className="h-px bg-[#8a8680]/10" data-id="element-1483" /></>}</>}
          <div className="flex items-center justify-between py-4" data-id="element-1484"><span className="text-sm text-[#8a8680]" data-id="element-1485">Filters</span><div className="flex flex-wrap gap-2 items-center" data-id="element-1486">{filters.map(f => <span key={f.id} className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm bg-[#252530] text-[#8a8680]" data-id="element-1487">{f.label}<button onClick={() => removeFilter(f.id)} data-id="element-1488"><X className="w-3 h-3" data-id="element-1489" /></button></span>)}<div className="relative" data-id="element-1490"><button onClick={() => setShowFilterMenu(!showFilterMenu)} className="text-[#8a8680] hover:text-[#e8e4df]" data-id="element-1491"><Plus size={14} data-id="element-1492" /></button>{showFilterMenu && <><div className="fixed inset-0 z-10" onClick={() => setShowFilterMenu(false)} data-id="element-1493" /><div className="absolute right-0 mt-2 rounded-lg z-20 w-[180px] bg-[#252530] max-h-[240px] overflow-y-auto" data-id="element-1494">{availableFilters.filter(f => !filters.find(ef => ef.label === f)).map(f => <button key={f} onClick={() => addFilter(f)} className="w-full text-left px-3 py-2 text-sm text-[#e8e4df] hover:bg-[#4a9a9a]/10" data-id="element-1495">{f}</button>)}</div></>}</div></div></div>
          <div className="h-px bg-[#8a8680]/10" data-id="element-1496" />
          {listType === 'standard' && <><div className="flex items-center justify-between py-4" data-id="element-1497"><span className="text-sm text-[#8a8680]" data-id="element-1498">Sort</span><Select value={sort} onChange={setSort} options={availableSorts} data-id="element-1499" /></div><div className="h-px bg-[#8a8680]/10" data-id="element-1500" /></>}
          <div className="flex items-center justify-between py-4" data-id="element-1501"><span className="text-sm text-[#8a8680]" data-id="element-1502">Group by</span><Select value={groupBy} onChange={setGroupBy} options={availableGroups} data-id="element-1503" /></div><div className="h-px bg-[#8a8680]/10" data-id="element-1504" />
          <div className="flex items-center justify-between py-4" data-id="element-1505"><span className="text-sm text-[#8a8680]" data-id="element-1506">Limit</span><input type="number" value={limit} onChange={e => setLimit(parseInt(e.target.value) || 0)} className="w-20 px-3 py-1.5 rounded bg-[#252530] text-[#e8e4df] outline-none border-none" data-id="element-1507" /></div>
        </div>
        <div className="flex justify-center gap-6" data-id="element-1508"><button className="text-[14px] text-[#4a9a9a]/80 hover:text-[#4a9a9a]" data-id="element-1509">{list ? 'Save' : 'Create'}</button><button onClick={onBack} className="text-[14px] text-[#8a8680]/50 hover:text-[#e8e4df]/80" data-id="element-1510">Cancel</button></div>
      </div>
    </motion.div>;
}
function Select<T extends string>({
  value,
  onChange,
  options
}: {
  value: T;
  onChange: (v: T) => void;
  options: T[];
}) {
  const [isOpen, setIsOpen] = useState(false);
  return <div className="relative" data-id="element-1511"><button onClick={() => setIsOpen(!isOpen)} className="flex items-center gap-2 px-3 py-1.5 rounded min-w-[140px] justify-between bg-[#252530] text-[#e8e4df]" data-id="element-1512"><span className="text-sm capitalize" data-id="element-1513">{value}</span><ChevronDown className="w-4 h-4 text-[#8a8680]" data-id="element-1514" /></button>{isOpen && <><div className="fixed inset-0 z-10" onClick={() => setIsOpen(false)} data-id="element-1515" /><div className="absolute right-0 mt-2 rounded-lg z-20 min-w-[140px] bg-[#252530]" data-id="element-1516">{options.map(o => <button key={o} onClick={() => {
          onChange(o);
          setIsOpen(false);
        }} className="w-full text-left px-3 py-2 text-sm hover:bg-[#4a9a9a]/10 capitalize" style={{
          color: value === o ? '#4a9a9a' : '#e8e4df'
        }} data-id="element-1517">{o}</button>)}</div></>}</div>;
}